import { z } from 'zod';
import {
  BOARD_TITLE_MAX_LENGTH,
  CARD_TITLE_MAX_LENGTH,
  SHORT_LINK_MAX_LENGTH,
} from './board';
import { CARD_DESCRIPTION_MAX_LENGTH } from './card-detail';
import { CHECKLIST_ITEM_TEXT_MAX_LENGTH, CHECKLIST_TITLE_MAX_LENGTH } from './checklist';
import { COMMENT_BODY_MAX_LENGTH } from './comment';
import { ApiError } from './errors';
import { ulidSchema } from './id';
import { LIST_TITLE_MAX_LENGTH } from './list';
import { RANK_MAX_LENGTH } from './rank';
import { publicProfileSchema } from './user';

/**
 * Automation contracts - B68, `FR-AUTO-001`, `FR-AUTO-003`-`FR-AUTO-006`.
 *
 * These schemas describe the board-level command surface and the strict command
 * definition grammar. The closed vocabularies mirror the existing CHECK
 * constraints on the automation tables created in migration 0008.
 */

export const AUTOMATION_COMMAND_KINDS = [
  'rule',
  'card_button',
  'board_button',
  'calendar_command',
  'due_date_command',
] as const;

export const AUTOMATION_TRIGGER_KINDS = [
  'card_added_to_list',
  'card_moved_into_list',
  'card_moved_out_of_list',
  'card_archived',
  'card_unarchived',
  'label_added',
  'label_removed',
  'member_joined_card',
  'member_left_card',
  'due_date_added',
  'due_date_changed',
  'due_date_removed',
  'due_date_marked_complete',
  'checklist_added',
  'checklist_completed',
  'checklist_item_checked',
  'checklist_item_unchecked',
  'comment_posted',
  'comment_matching_text_posted',
  'attachment_added',
  'card_voted_on',
  'custom_field_value_changed',
  'card_name_matches',
  'list_created',
  'list_renamed',
  'list_archived',
  'schedule_reached',
  'due_date_offset_reached',
] as const;

export const AUTOMATION_RULE_TRIGGER_KINDS = [
  'card_added_to_list',
  'card_moved_into_list',
  'card_moved_out_of_list',
  'card_archived',
  'card_unarchived',
  'label_added',
  'label_removed',
  'member_joined_card',
  'member_left_card',
  'due_date_added',
  'due_date_changed',
  'due_date_removed',
  'due_date_marked_complete',
  'checklist_added',
  'checklist_completed',
  'checklist_item_checked',
  'checklist_item_unchecked',
  'comment_posted',
  'comment_matching_text_posted',
  'attachment_added',
  'card_voted_on',
  'custom_field_value_changed',
  'card_name_matches',
  'list_created',
  'list_renamed',
  'list_archived',
] as const;

export const AUTOMATION_CONDITION_KINDS = ['actor', 'card_state', 'text_match'] as const;

export const AUTOMATION_CONDITION_OPERATORS = [
  'is',
  'is_not',
  'has',
  'has_not',
  'contains',
  'not_contains',
] as const;

export const AUTOMATION_ACTION_KINDS = [
  'move_card',
  'copy_card',
  'mirror_card',
  'archive_card',
  'unarchive_card',
  'delete_card',
  'rename_card',
  'set_due_date',
  'move_due_date',
  'remove_due_date',
  'mark_due_date_complete',
  'add_label',
  'remove_label',
  'add_member',
  'remove_member',
  'post_comment',
  'create_card',
  'create_list',
  'rename_list',
  'sort_list',
  'move_all_cards_in_list',
  'add_checklist',
  'remove_checklist',
  'check_checklist_item',
  'uncheck_checklist_item',
  'set_custom_field_value',
  'send_email',
  'http_request',
] as const;

/**
 * The curly-brace variables an automation's free-text blanks accept —
 * `FR-AUTO-006`.
 *
 * ⚠ **This list exists because it used to exist twice.** The editor kept a
 * private `VARIABLES` array and `apps/server/src/automation/variables.ts` kept
 * a resolver, and nothing held the two together. Fan-out 9 measured them: every
 * token the editor could insert was a name the resolver could resolve, so the
 * lists happened to agree — but only by hand, and a token added to one side and
 * not the other resolves to the **empty string**, not to an error. `FR-AUTO-006`
 * AC3 requires exactly that ("a variable with no value in context … resolves to
 * empty rather than failing the whole run"), which is right for a variable with
 * no *value* and catastrophic for a variable with no *implementation*: the two
 * are indistinguishable at run time and both look like a blank in a comment.
 *
 * So the editor renders this array, and `AutomationEditor.test.tsx` asserts the
 * rendered inserter against it one for one and in order. That pins
 * **render <-> catalogue**: a token dropped from the render, or rendered but not
 * listed here, fails a test.
 *
 * ⚠ **`catalogue <-> resolver` is NOT pinned, and cannot be pinned from this
 * package** — `apps/server/src/automation/variables.ts` is not importable from
 * `packages/shared`, and the dependency may not run the other way either. So a
 * token added to this array with no resolver arm still renders, still saves, and
 * still resolves to the empty string. That pin is a **residual owned by
 * `apps/server`**, and the shape it has to take is narrow: assert every token
 * has a resolver *arm*, not that every token resolves *non-empty*. Six tokens
 * (`{commenttext}`, `{checklistitemname}`, `{customfieldvalue}`,
 * `{newcardname}`, `{foundcardname}`, `{copycardname}`) are legitimately empty
 * until a trigger or a multi-card action family fills them, and the dotted entry
 * below is a shape rather than a name — so a naive "no token survives as literal
 * braces" test is red on seven of these twenty-three and would be loosened to go
 * green.
 *
 * `{httpresponse.property}` is the one entry that is a *shape* rather than a
 * name — the resolver reads any dotted path under `httpresponse`, and this is
 * the inserter's way of showing that.
 */
export const AUTOMATION_TEXT_VARIABLES = [
  '{date}',
  '{time}',
  '{weekdayname}',
  '{tomorrowdate}',
  '{yesterdaydate}',
  '{cardname}',
  '{cardlink}',
  '{cardlabelnames}',
  '{cardmemberusernames}',
  '{cardagedays}',
  '{boardname}',
  '{boardlink}',
  '{username}',
  '{fullname}',
  '{commenttext}',
  '{checklistitemname}',
  '{customfieldvalue}',
  '{newcardname}',
  '{triggercardname}',
  '{foundcardname}',
  '{copycardname}',
  '{httpresponse}',
  '{httpresponse.property}',
] as const;

export const AUTOMATION_COMMAND_VISIBILITIES = ['shared', 'private'] as const;

export const AUTOMATION_COMMAND_SCOPE_KINDS = [
  'single_board',
  'all_administrable_boards',
] as const;

export const AUTOMATION_SCHEDULE_FREQUENCIES = [
  'daily',
  'weekdays',
  'weekly',
  'monthly',
  'yearly',
] as const;

export const AUTOMATION_RUN_ENTRY_KINDS = [
  'run',
  'quota_blocked',
  'permission_blocked',
  'missing_reference_blocked',
  'cap_exceeded',
  'loop_suppressed',
  'definition_changed',
  'admin_disabled',
] as const;

export const AUTOMATION_RUN_OUTCOMES = ['succeeded', 'warning', 'failed'] as const;

export const AUTOMATION_DIAGNOSTIC_REASONS = [
  'disabled',
  'quota_exhausted',
  'condition_excluded',
  'permission_denied',
  'missing_reference',
  'cap_exceeded',
  'loop_suppressed',
  'acting_member_missing',
  'admin_disabled',
] as const;

export const AUTOMATION_REFERENCE_KINDS = [
  'board',
  'list',
  'label',
  'member',
  'card',
  'checklist',
  'checklist_item',
  'custom_field',
] as const;

export const AUTOMATION_UNRESOLVED_REFERENCE_REASONS = [
  'not_found_on_destination_board',
  'ambiguous_on_destination_board',
  'not_copyable_to_destination_board',
] as const;

export const automationCommandKindValues = AUTOMATION_COMMAND_KINDS;
export const automationTriggerKindValues = AUTOMATION_TRIGGER_KINDS;
export const automationConditionKindValues = AUTOMATION_CONDITION_KINDS;
export const automationConditionOperatorValues = AUTOMATION_CONDITION_OPERATORS;
export const automationActionKindValues = AUTOMATION_ACTION_KINDS;
export const automationRunEntryKindValues = AUTOMATION_RUN_ENTRY_KINDS;
export const automationRunOutcomeValues = AUTOMATION_RUN_OUTCOMES;
export const automationCommandVisibilityValues = AUTOMATION_COMMAND_VISIBILITIES;
export const automationDiagnosticReasonValues = AUTOMATION_DIAGNOSTIC_REASONS;
export const automationReferenceKindValues = AUTOMATION_REFERENCE_KINDS;

export const AUTOMATION_COMMAND_NAME_MAX_LENGTH = 255;
export const AUTOMATION_ICON_MAX_LENGTH = 64;
export const AUTOMATION_TEXT_MATCH_MAX_LENGTH = 255;
export const AUTOMATION_HTTP_URL_MAX_LENGTH = 2048;
export const AUTOMATION_EMAIL_ADDRESS_MAX_LENGTH = 320;
export const AUTOMATION_EMAIL_SUBJECT_MAX_LENGTH = 998;
export const AUTOMATION_TIMEZONE_MAX_LENGTH = 64;
export const AUTOMATION_ACTION_SUMMARY_MAX_LENGTH = 512;
export const AUTOMATION_DIAGNOSTIC_DETAIL_MAX_LENGTH = 2048;
export const AUTOMATION_LOG_PAGE_SIZE_DEFAULT = 25;
export const AUTOMATION_LOG_PAGE_SIZE_MAX = 100;
export const AUTOMATION_MONTHLY_RUN_LIMIT = 1_000;
export const AUTOMATION_MONTHLY_OPERATION_LIMIT = 20_000;
export const AUTOMATION_MONTHLY_EMAIL_LIMIT = 1_000;
export const AUTOMATION_ACTION_LIMIT = 20;
/**
 * `FR-AUTO-020` Behaviour: *"A member may keep at most **20 card buttons and 20
 * board buttons** active at once"*, and AC4: *"Given I have 20 active board
 * buttons, when I create another, then it is saved but cannot be activated
 * until one is deactivated."*
 *
 * The cap is **per kind** — twenty card buttons *and* twenty board buttons, not
 * twenty buttons — so one constant serves both counts and the caller supplies
 * the kind it is counting.
 *
 * ⚠ **THIS IS THE ONLY PRODUCT COPY OF THE NUMBER.** Earlier frontend and
 * server slices carried independent private literals that had already drifted
 * in *name*. The consequence of a drift was an authoring UI that let a member
 * activate a twenty-first button and a server that refused it, or a UI warning
 * at a cap the server did not enforce. Both sides now import this constant.
 */
export const AUTOMATION_ACTIVE_BUTTON_LIMIT = 20;
export const AUTOMATION_EMAIL_RECIPIENT_LIMIT = 50;
export const AUTOMATION_DEFINITION_TEXT_MAX_LENGTH = 64_000;
export const AUTOMATION_CHAIN_DEPTH_LIMIT = 5;
export const AUTOMATION_HTTP_TIMEOUT_MS = 10_000;
export const AUTOMATION_HTTP_ATTEMPTS = 3;

export const automationCommandKindSchema = z.enum(AUTOMATION_COMMAND_KINDS);
export type AutomationCommandKind = z.infer<typeof automationCommandKindSchema>;

export const automationTriggerKindSchema = z.enum(AUTOMATION_TRIGGER_KINDS);
export type AutomationTriggerKind = z.infer<typeof automationTriggerKindSchema>;

export const automationRuleTriggerKindSchema = z.enum(AUTOMATION_RULE_TRIGGER_KINDS);
export type AutomationRuleTriggerKind = z.infer<typeof automationRuleTriggerKindSchema>;

/**
 * Why a catalogue trigger kind cannot fire yet - `FR-AUTO-003`, `BE-68.11`.
 *
 * `FR-AUTO-003` names both `checklist_completed` (*"a checklist is completed"*)
 * and `card_voted_on` (*"a card is voted on"*) in its minimum catalogue, and
 * AC2 requires the first of them to fire. `card_voted_on` fires since `BE-68.8`
 * (votes write a `card.voted_on` SC-1 activity entry and the matcher's `switch`
 * derives the candidate); `checklist_completed` fires since `BE-68.9` (the
 * checklist-item route hydrates `TriggerDerivationContext.checklistBecameComplete`
 * and the matcher's `switch` derives the candidate behind it). The dispatcher
 * fires **all 28** kinds in `AUTOMATION_TRIGGER_KINDS` and **all 26** in
 * `AUTOMATION_RULE_TRIGGER_KINDS`; the map below is empty and stays as the
 * place a future inert kind is marked.
 *
 * ⚠ **THE TWO WERE INERT FOR DIFFERENT REASONS, WHICH IS WHY THIS IS AN ENUM
 * AND NOT ONE SHARED SENTENCE.** They close by completely different work and a
 * single string would make them indistinguishable to a reader and to a test:
 *
 * - `card_voted_on` - votes wrote **no SC-1 activity entry**, so no event ever
 *   derived the candidate. There was no `card.voted_on` in `EVENT_TYPE_NAMES`
 *   and no arm for it in the matcher's `switch` at all. Closed by `BE-68.8`,
 *   which deleted its entry below.
 * - `checklist_completed` - the event exists **and the matcher does derive
 *   it**, but only behind `TriggerDerivationContext.checklistBecameComplete`.
 *   Threading that flag through the generic dispatcher would have meant an
 *   aggregate over `checklist_items` on every checked box in the product, so
 *   instead the checklist-item route hydrates it per event (`BE-68.9`) and the
 *   dispatcher carries it as a pass-through. Closed by `BE-68.9`, which deleted
 *   its entry below.
 *
 * ⚠ **Both codes are retained with no kind using either, deliberately**
 * (`no_activity_event` since `BE-68.8`, `no_completion_aggregate` since
 * `BE-68.9`).
 * Deleting the code as well as the entry would fail the total `Record`s in
 * `apps/web/.../AutomationEditor.tsx`, `BoardAutomationSection.tsx` and
 * `apps/server/src/automation/triggers.ts` to compile, forcing a web source
 * change for a sentence no surface can reach any more — and the B80 catalogue
 * key with it. A future trigger whose event does not exist yet reuses this
 * code instead of re-declaring it. `packages/shared/src/automation.test.ts`
 * pins the code as declared-but-unused, so the retention cannot drift into
 * silent reuse.
 *
 * A consequence worth stating, because it would pass today while asserting
 * something false: **a pin that derives this map from "kinds absent from the
 * matcher switch" is wrong.** `checklist_completed` is in the switch. Pin this
 * map against itself and against the picker, never against the switch.
 *
 * ⚠ **THIS IS A STATIC DECLARATION AND NOT A WIRE FIELD, AND THE REASON IS
 * TIMING RATHER THAN TASTE.** The obvious design - hang the reason off
 * `automationTriggerSchema` - cannot serve the surface that needs it: the
 * picker in `apps/web/src/components/board/AutomationEditor.tsx` renders
 * `AUTOMATION_RULE_TRIGGER_KINDS.map(...)` while authoring a **new** rule, so
 * there is no stored trigger to carry a reason at the moment the picker must
 * tell the truth. A field there would also have reached all five response
 * projections in `apps/server/src/routes/automations.ts` (`GET`/`POST`
 * `/boards/:boardId/automations`, `GET`/`PATCH` `/automations/:commandId` and
 * `POST /automations/:commandId/duplicate`), 20 e2e `.parse()` sites and 3 web
 * call sites, for information that is a build-time constant in a monorepo that
 * ships client and server together. Nothing on the wire changes because of
 * this family.
 *
 * ⚠ **Not to be confused with `AUTOMATION_DIAGNOSTIC_REASONS` or `isBroken`.**
 * Every arm of that vocabulary is a per-command, per-run fact - the nearest,
 * `missing_reference`, means a list or label *this* command points at has gone,
 * not that a whole trigger kind has no engine. And `isBroken` is a
 * **suppression flag** read as a filter by `dispatch.ts` and both schedule
 * sweeps, so marking an inert rule broken would disable it: a real product
 * change applied to a rule that is merely waiting on `BE-68.8`. The
 * unsupported-trigger fact is a property of the **catalogue**, not of any
 * command, which is why it lives here.
 *
 * The codes are codes, not English: the sentences live in the B80 interface
 * language catalogue, because no snake_case wire word may reach a rendered
 * title.
 *
 * ⚠ **An entry here does NOT make a route refuse the kind.** `FR-AUTO-003`
 * requires these triggers to exist and AC2 requires one of them to fire, the
 * `ck_automation_triggers_trigger_kind` CHECK has always admitted both, and
 * rules carrying them may already be stored - a refusing `PATCH` would make
 * such a rule uneditable, unable even to be renamed. Honesty is annotation,
 * not refusal.
 */
export const AUTOMATION_UNSUPPORTED_TRIGGER_REASON_CODES = [
  'no_activity_event',
  'no_completion_aggregate',
] as const;

export const automationUnsupportedTriggerReasonCodeSchema = z.enum(
  AUTOMATION_UNSUPPORTED_TRIGGER_REASON_CODES,
);
export type AutomationUnsupportedTriggerReasonCode = z.infer<
  typeof automationUnsupportedTriggerReasonCodeSchema
>;

/**
 * The trigger kinds that cannot fire, and why. **Absent means supported** -
 * this map is deliberately partial so that closing `BE-68.8` and `BE-68.9` was
 * a deletion here rather than an edit to a parallel list. Both entries are now
 * deleted, so the map is empty: every catalogue kind fires. The empty literal
 * stays (rather than the export going away) so a future inert kind re-adds an
 * entry here and every derived surface follows without a shape change.
 *
 * The `Partial<Record<AutomationTriggerKind, ...>>` annotation is load-bearing
 * and not decoration: without it a mistyped kind key would compile into a
 * silently inert entry, which is the exact defect shape this family exists to
 * remove.
 *
 * `apps/server/src/automation/triggers.ts` re-bases its own
 * `UNSUPPORTED_TRIGGER_REASONS` on this map so the two key sets cannot drift,
 * following the `AUTOMATION_ACTIVE_BUTTON_LIMIT` precedent above.
 */
export const AUTOMATION_UNSUPPORTED_TRIGGER_KINDS: Partial<
  Record<AutomationTriggerKind, AutomationUnsupportedTriggerReasonCode>
> = {};

/**
 * `null` when the kind fires today. `AutomationRuleTriggerKind` is a subset of
 * `AutomationTriggerKind`, so the picker's kinds are accepted here without a
 * second helper.
 */
export function unsupportedAutomationTriggerReason(
  kind: AutomationTriggerKind,
): AutomationUnsupportedTriggerReasonCode | null {
  return AUTOMATION_UNSUPPORTED_TRIGGER_KINDS[kind] ?? null;
}

export const automationConditionKindSchema = z.enum(AUTOMATION_CONDITION_KINDS);
export type AutomationConditionKind = z.infer<typeof automationConditionKindSchema>;

export const automationConditionOperatorSchema = z.enum(AUTOMATION_CONDITION_OPERATORS);
export type AutomationConditionOperator = z.infer<
  typeof automationConditionOperatorSchema
>;

export const automationActionKindSchema = z.enum(AUTOMATION_ACTION_KINDS);
export type AutomationActionKind = z.infer<typeof automationActionKindSchema>;

export const automationCommandVisibilitySchema = z.enum(AUTOMATION_COMMAND_VISIBILITIES);
export type AutomationCommandVisibility = z.infer<
  typeof automationCommandVisibilitySchema
>;

export const automationCommandScopeKindSchema = z.enum(AUTOMATION_COMMAND_SCOPE_KINDS);
export type AutomationCommandScopeKind = z.infer<typeof automationCommandScopeKindSchema>;

export const automationScheduleFrequencySchema = z.enum(AUTOMATION_SCHEDULE_FREQUENCIES);
export type AutomationScheduleFrequency = z.infer<
  typeof automationScheduleFrequencySchema
>;

export const automationRunEntryKindSchema = z.enum(AUTOMATION_RUN_ENTRY_KINDS);
export type AutomationRunEntryKind = z.infer<typeof automationRunEntryKindSchema>;

export const automationRunOutcomeSchema = z.enum(AUTOMATION_RUN_OUTCOMES);
export type AutomationRunOutcome = z.infer<typeof automationRunOutcomeSchema>;

export const automationDiagnosticReasonSchema = z.enum(AUTOMATION_DIAGNOSTIC_REASONS);
export type AutomationDiagnosticReason = z.infer<typeof automationDiagnosticReasonSchema>;

export const automationReferenceKindSchema = z.enum(AUTOMATION_REFERENCE_KINDS);
export type AutomationReferenceKind = z.infer<typeof automationReferenceKindSchema>;

export const automationUnresolvedReferenceReasonSchema = z.enum(
  AUTOMATION_UNRESOLVED_REFERENCE_REASONS,
);
export type AutomationUnresolvedReferenceReason = z.infer<
  typeof automationUnresolvedReferenceReasonSchema
>;

const timestampViewSchema = z.string().min(1).max(64);
const dateViewSchema = z.string().min(1).max(64);
const rankViewSchema = z.string().min(1).max(RANK_MAX_LENGTH);
const commandNameInputSchema = z
  .string()
  .trim()
  .min(1)
  .max(AUTOMATION_COMMAND_NAME_MAX_LENGTH);
const commandNameViewSchema = z.string().min(1).max(AUTOMATION_COMMAND_NAME_MAX_LENGTH);
const boardNameViewSchema = z.string().min(1).max(BOARD_TITLE_MAX_LENGTH);
const plainTextInputSchema = z
  .string()
  .trim()
  .min(1)
  .max(AUTOMATION_DEFINITION_TEXT_MAX_LENGTH);
const plainTextViewSchema = z.string().min(1);
const cursorViewSchema = z.string().min(1).max(128);
const detailTextViewSchema = z
  .string()
  .min(1)
  .max(AUTOMATION_DIAGNOSTIC_DETAIL_MAX_LENGTH);
const actionSummaryViewSchema = z
  .string()
  .min(1)
  .max(AUTOMATION_ACTION_SUMMARY_MAX_LENGTH);
const iconInputSchema = z.string().trim().min(1).max(AUTOMATION_ICON_MAX_LENGTH);
const iconViewSchema = z.string().min(1).max(AUTOMATION_ICON_MAX_LENGTH);
const textMatchInputSchema = z
  .string()
  .trim()
  .min(1)
  .max(AUTOMATION_TEXT_MATCH_MAX_LENGTH);
const variableTextSchema = z.string().min(1);
const cardTitleInputSchema = z.string().trim().min(1).max(CARD_TITLE_MAX_LENGTH);
const listTitleInputSchema = z.string().trim().min(1).max(LIST_TITLE_MAX_LENGTH);
const checklistTitleInputSchema = z
  .string()
  .trim()
  .min(1)
  .max(CHECKLIST_TITLE_MAX_LENGTH);
const checklistItemTextInputSchema = z
  .string()
  .trim()
  .min(1)
  .max(CHECKLIST_ITEM_TEXT_MAX_LENGTH);
const requestDateTimeSchema = z.iso.datetime({ offset: true });
const scheduleTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/);
const scheduleMonthDaySchema = z
  .string()
  .regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/);
const ianaTimezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(AUTOMATION_TIMEZONE_MAX_LENGTH)
  .refine(
    (value) => {
      try {
        Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date(0));
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Expected an IANA time zone' },
  );

export const automationWeekdaySchema = z.enum([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]);
export type AutomationWeekday = z.infer<typeof automationWeekdaySchema>;

export const automationScheduleSchema = z.object({
  frequency: automationScheduleFrequencySchema,
  daysOfWeek: z.array(automationWeekdaySchema),
  dayOfMonth: z.number().int().min(0).max(31).nullable(),
  monthDay: scheduleMonthDaySchema.nullable(),
  time: scheduleTimeSchema,
  timezone: z.string().min(1).max(AUTOMATION_TIMEZONE_MAX_LENGTH),
});
export type AutomationSchedule = z.infer<typeof automationScheduleSchema>;

export const automationScheduleBodySchema = z.strictObject({
  frequency: automationScheduleFrequencySchema,
  daysOfWeek: z.array(automationWeekdaySchema).default([]),
  dayOfMonth: z.number().int().min(1).max(31).nullable().default(null),
  monthDay: scheduleMonthDaySchema.nullable().default(null),
  time: scheduleTimeSchema,
  timezone: ianaTimezoneSchema,
});
export type AutomationScheduleBody = z.infer<typeof automationScheduleBodySchema>;

export const automationActorConditionSubjectSchema = z.enum([
  'acting_member',
  'specific_member',
]);
export type AutomationActorConditionSubject = z.infer<
  typeof automationActorConditionSubjectSchema
>;

export const automationCardStateConditionFieldSchema = z.enum([
  'label',
  'member',
  'due_date',
  'list_name',
  'card_name',
]);
export type AutomationCardStateConditionField = z.infer<
  typeof automationCardStateConditionFieldSchema
>;

export const automationTextMatchConditionSourceSchema = z.enum([
  'card_name',
  'comment_body',
]);
export type AutomationTextMatchConditionSource = z.infer<
  typeof automationTextMatchConditionSourceSchema
>;

export const automationConditionDefinitionSchema = z.union([
  z.strictObject({
    conditionKind: z.literal('actor'),
    operator: z.enum(['is', 'is_not']),
    subject: z.literal('acting_member'),
  }),
  z.strictObject({
    conditionKind: z.literal('actor'),
    operator: z.enum(['is', 'is_not']),
    subject: z.literal('specific_member'),
    memberUserId: ulidSchema,
  }),
  z.strictObject({
    conditionKind: z.literal('card_state'),
    field: z.literal('label'),
    operator: z.enum(['has', 'has_not']),
    labelId: ulidSchema,
  }),
  z.strictObject({
    conditionKind: z.literal('card_state'),
    field: z.literal('member'),
    operator: z.enum(['has', 'has_not']),
    memberUserId: ulidSchema,
  }),
  z.strictObject({
    conditionKind: z.literal('card_state'),
    field: z.literal('due_date'),
    operator: z.enum(['has', 'has_not']),
  }),
  z.strictObject({
    conditionKind: z.literal('card_state'),
    field: z.literal('list_name'),
    operator: z.enum(['contains', 'not_contains']),
    textValue: textMatchInputSchema,
  }),
  z.strictObject({
    conditionKind: z.literal('card_state'),
    field: z.literal('card_name'),
    operator: z.enum(['contains', 'not_contains']),
    textValue: textMatchInputSchema,
  }),
  z.strictObject({
    conditionKind: z.literal('text_match'),
    source: automationTextMatchConditionSourceSchema,
    operator: z.enum(['contains', 'not_contains']),
    textValue: textMatchInputSchema,
  }),
]);
export type AutomationConditionDefinition = z.infer<
  typeof automationConditionDefinitionSchema
>;

export const automationConditionBodySchema = automationConditionDefinitionSchema;
export type AutomationConditionBody = z.infer<typeof automationConditionBodySchema>;

const automationConditionViewBaseFields = {
  id: ulidSchema,
  triggerId: ulidSchema,
  createdAt: timestampViewSchema,
  updatedAt: timestampViewSchema,
} as const;

export const automationConditionSchema = z.union([
  z.object({
    ...automationConditionViewBaseFields,
    conditionKind: automationConditionKindSchema,
    operator: automationConditionOperatorSchema,
    memberUserId: ulidSchema.nullable(),
    labelId: ulidSchema.nullable(),
    textValue: z.string().max(AUTOMATION_TEXT_MATCH_MAX_LENGTH).nullable(),
  }),
  z.object({
    ...automationConditionViewBaseFields,
    conditionKind: z.literal('actor'),
    operator: z.enum(['is', 'is_not']),
    subject: z.literal('acting_member'),
  }),
  z.object({
    ...automationConditionViewBaseFields,
    conditionKind: z.literal('actor'),
    operator: z.enum(['is', 'is_not']),
    subject: z.literal('specific_member'),
    memberUserId: ulidSchema,
  }),
  z.object({
    ...automationConditionViewBaseFields,
    conditionKind: z.literal('card_state'),
    field: z.literal('label'),
    operator: z.enum(['has', 'has_not']),
    labelId: ulidSchema,
  }),
  z.object({
    ...automationConditionViewBaseFields,
    conditionKind: z.literal('card_state'),
    field: z.literal('member'),
    operator: z.enum(['has', 'has_not']),
    memberUserId: ulidSchema,
  }),
  z.object({
    ...automationConditionViewBaseFields,
    conditionKind: z.literal('card_state'),
    field: z.literal('due_date'),
    operator: z.enum(['has', 'has_not']),
  }),
  z.object({
    ...automationConditionViewBaseFields,
    conditionKind: z.literal('card_state'),
    field: z.literal('list_name'),
    operator: z.enum(['contains', 'not_contains']),
    textValue: z.string().min(1).max(AUTOMATION_TEXT_MATCH_MAX_LENGTH),
  }),
  z.object({
    ...automationConditionViewBaseFields,
    conditionKind: z.literal('card_state'),
    field: z.literal('card_name'),
    operator: z.enum(['contains', 'not_contains']),
    textValue: z.string().min(1).max(AUTOMATION_TEXT_MATCH_MAX_LENGTH),
  }),
  z.object({
    ...automationConditionViewBaseFields,
    conditionKind: z.literal('text_match'),
    source: automationTextMatchConditionSourceSchema,
    operator: z.enum(['contains', 'not_contains']),
    textValue: z.string().min(1).max(AUTOMATION_TEXT_MATCH_MAX_LENGTH),
  }),
]);
export type AutomationCondition = z.infer<typeof automationConditionSchema>;

const automationTriggerBaseBodyFields = {
  targetListId: ulidSchema.nullable().default(null),
  targetLabelId: ulidSchema.nullable().default(null),
  textMatch: textMatchInputSchema.nullable().default(null),
  conditions: z.array(automationConditionDefinitionSchema).default([]),
} as const;

export const automationTriggerDefinitionSchema = z.strictObject({
  triggerKind: automationTriggerKindSchema,
  ...automationTriggerBaseBodyFields,
});
export type AutomationTriggerDefinition = z.infer<
  typeof automationTriggerDefinitionSchema
>;

export const automationTriggerBodySchema = automationTriggerDefinitionSchema;
export type AutomationTriggerBody = z.infer<typeof automationTriggerBodySchema>;

export const automationRuleTriggerBodySchema = z.strictObject({
  triggerKind: automationRuleTriggerKindSchema,
  ...automationTriggerBaseBodyFields,
});
export type AutomationRuleTriggerBody = z.infer<typeof automationRuleTriggerBodySchema>;

export const automationCalendarTriggerBodySchema = z.strictObject({
  triggerKind: z.literal('schedule_reached'),
  ...automationTriggerBaseBodyFields,
});
export type AutomationCalendarTriggerBody = z.infer<
  typeof automationCalendarTriggerBodySchema
>;

export const automationDueDateTriggerBodySchema = z.strictObject({
  triggerKind: z.literal('due_date_offset_reached'),
  ...automationTriggerBaseBodyFields,
});
export type AutomationDueDateTriggerBody = z.infer<
  typeof automationDueDateTriggerBodySchema
>;

export const automationTriggerSchema = z.object({
  id: ulidSchema,
  commandId: ulidSchema,
  targetListId: ulidSchema.nullable(),
  targetLabelId: ulidSchema.nullable(),
  triggerKind: automationTriggerKindSchema,
  textMatch: z.string().max(AUTOMATION_TEXT_MATCH_MAX_LENGTH).nullable(),
  conditions: z.array(automationConditionSchema),
  createdAt: timestampViewSchema,
  updatedAt: timestampViewSchema,
});
export type AutomationTrigger = z.infer<typeof automationTriggerSchema>;

export const automationCardPlacementSchema = z.enum(['top', 'bottom', 'position']);
export type AutomationCardPlacement = z.infer<typeof automationCardPlacementSchema>;

export const automationListSortFieldSchema = z.enum([
  'name',
  'due_date',
  'created_date',
  'votes',
]);
export type AutomationListSortField = z.infer<typeof automationListSortFieldSchema>;

export const automationSortDirectionSchema = z.enum(['ascending', 'descending']);
export type AutomationSortDirection = z.infer<typeof automationSortDirectionSchema>;

export const automationHttpMethodSchema = z.enum(['GET', 'POST', 'PUT']);
export type AutomationHttpMethod = z.infer<typeof automationHttpMethodSchema>;

const noActionParametersSchema = z.strictObject({});

const cardPlacementParametersSchema = z.strictObject({
  placement: automationCardPlacementSchema.default('bottom'),
  previousCardId: ulidSchema.nullable().default(null),
  nextCardId: ulidSchema.nullable().default(null),
});

const renameCardParametersSchema = z.strictObject({
  title: cardTitleInputSchema,
});

const dueDateParametersSchema = z.discriminatedUnion('mode', [
  z.strictObject({
    mode: z.literal('absolute'),
    dueAt: requestDateTimeSchema,
    dueReminderMinutesBefore: z.number().int().nonnegative().nullable().default(null),
  }),
  z.strictObject({
    mode: z.literal('relative_days'),
    daysFromNow: z.number().int().min(-3650).max(3650),
    dueReminderMinutesBefore: z.number().int().nonnegative().nullable().default(null),
  }),
]);

const moveDueDateParametersSchema = z.strictObject({
  days: z.number().int().min(-3650).max(3650),
});

const memberParametersSchema = z.strictObject({
  memberUserId: ulidSchema.nullable().default(null),
  useActingMember: z.boolean().default(false),
});

const postCommentParametersSchema = z.strictObject({
  body: z.string().trim().min(1).max(COMMENT_BODY_MAX_LENGTH),
});

const createCardParametersSchema = z.strictObject({
  title: cardTitleInputSchema,
  description: z.string().max(CARD_DESCRIPTION_MAX_LENGTH).default(''),
});

const createListParametersSchema = z.strictObject({
  title: listTitleInputSchema,
});

const renameListParametersSchema = z.strictObject({
  title: listTitleInputSchema,
});

const sortListParametersSchema = z.strictObject({
  field: automationListSortFieldSchema,
  direction: automationSortDirectionSchema.default('ascending'),
});

const moveAllCardsInListParametersSchema = z.strictObject({
  sourceListId: ulidSchema,
});

const addChecklistParametersSchema = z.strictObject({
  title: checklistTitleInputSchema,
});

const removeChecklistParametersSchema = z.strictObject({
  checklistId: ulidSchema,
});

const checklistItemParametersSchema = z.strictObject({
  checklistId: ulidSchema,
  itemId: ulidSchema.nullable().default(null),
  itemText: checklistItemTextInputSchema.nullable().default(null),
});

const customFieldValueParametersSchema = z.strictObject({
  definitionId: ulidSchema,
  value: z.union([
    z.string().max(AUTOMATION_TEXT_MATCH_MAX_LENGTH),
    z.number().finite(),
    z.boolean(),
    z.null(),
  ]),
});

const sendEmailParametersSchema = z
  .strictObject({
    to: z.array(z.string().email().max(AUTOMATION_EMAIL_ADDRESS_MAX_LENGTH)).min(1),
    cc: z.array(z.string().email().max(AUTOMATION_EMAIL_ADDRESS_MAX_LENGTH)).default([]),
    subject: z.string().trim().min(1).max(AUTOMATION_EMAIL_SUBJECT_MAX_LENGTH),
    body: variableTextSchema,
  })
  .refine(
    (value) => value.to.length + value.cc.length <= AUTOMATION_EMAIL_RECIPIENT_LIMIT,
    {
      message: `An automation email action can address at most ${AUTOMATION_EMAIL_RECIPIENT_LIMIT} recipients.`,
      path: ['to'],
    },
  );

const httpRequestParametersSchema = z.strictObject({
  method: automationHttpMethodSchema,
  url: z.string().trim().min(1).max(AUTOMATION_HTTP_URL_MAX_LENGTH),
  headers: z.record(z.string().min(1), z.string()).default({}),
  body: z.string().default(''),
});

const automationActionBodyBaseFields = {
  position: rankViewSchema,
  targetBoardId: ulidSchema.nullable().default(null),
  targetListId: ulidSchema.nullable().default(null),
  targetLabelId: ulidSchema.nullable().default(null),
} as const;

const automationActionViewBaseFields = {
  id: ulidSchema,
  commandId: ulidSchema,
  position: rankViewSchema,
  targetBoardId: ulidSchema.nullable(),
  targetListId: ulidSchema.nullable(),
  targetLabelId: ulidSchema.nullable(),
  createdAt: timestampViewSchema,
  updatedAt: timestampViewSchema,
} as const;

const actionBodyVariant = <const Kind extends string>(
  actionKind: Kind,
  parameters: z.ZodType,
) =>
  z.strictObject({
    ...automationActionBodyBaseFields,
    actionKind: z.literal(actionKind),
    parameters,
  });

const actionViewVariant = <const Kind extends string>(
  actionKind: Kind,
  parameters: z.ZodType,
) =>
  z.object({
    ...automationActionViewBaseFields,
    actionKind: z.literal(actionKind),
    parameters,
  });

const actionSnapshotVariant = <const Kind extends string>(
  actionKind: Kind,
  parameters: z.ZodType,
) =>
  z.object({
    actionKind: z.literal(actionKind),
    actionPosition: rankViewSchema,
    targetBoardId: ulidSchema.nullable(),
    targetListId: ulidSchema.nullable(),
    targetLabelId: ulidSchema.nullable(),
    parameters,
    summary: actionSummaryViewSchema,
  });

export const automationActionDefinitionSchema = z.discriminatedUnion('actionKind', [
  actionBodyVariant('move_card', cardPlacementParametersSchema),
  actionBodyVariant('copy_card', cardPlacementParametersSchema),
  actionBodyVariant('mirror_card', cardPlacementParametersSchema),
  actionBodyVariant('archive_card', noActionParametersSchema),
  actionBodyVariant('unarchive_card', noActionParametersSchema),
  actionBodyVariant('delete_card', noActionParametersSchema),
  actionBodyVariant('rename_card', renameCardParametersSchema),
  actionBodyVariant('set_due_date', dueDateParametersSchema),
  actionBodyVariant('move_due_date', moveDueDateParametersSchema),
  actionBodyVariant('remove_due_date', noActionParametersSchema),
  actionBodyVariant('mark_due_date_complete', noActionParametersSchema),
  actionBodyVariant('add_label', noActionParametersSchema),
  actionBodyVariant('remove_label', noActionParametersSchema),
  actionBodyVariant('add_member', memberParametersSchema),
  actionBodyVariant('remove_member', memberParametersSchema),
  actionBodyVariant('post_comment', postCommentParametersSchema),
  actionBodyVariant('create_card', createCardParametersSchema),
  actionBodyVariant('create_list', createListParametersSchema),
  actionBodyVariant('rename_list', renameListParametersSchema),
  actionBodyVariant('sort_list', sortListParametersSchema),
  actionBodyVariant('move_all_cards_in_list', moveAllCardsInListParametersSchema),
  actionBodyVariant('add_checklist', addChecklistParametersSchema),
  actionBodyVariant('remove_checklist', removeChecklistParametersSchema),
  actionBodyVariant('check_checklist_item', checklistItemParametersSchema),
  actionBodyVariant('uncheck_checklist_item', checklistItemParametersSchema),
  actionBodyVariant('set_custom_field_value', customFieldValueParametersSchema),
  actionBodyVariant('send_email', sendEmailParametersSchema),
  actionBodyVariant('http_request', httpRequestParametersSchema),
]);
export type AutomationActionDefinition = z.infer<typeof automationActionDefinitionSchema>;

export const automationActionSchema = z.discriminatedUnion('actionKind', [
  actionViewVariant('move_card', cardPlacementParametersSchema),
  actionViewVariant('copy_card', cardPlacementParametersSchema),
  actionViewVariant('mirror_card', cardPlacementParametersSchema),
  actionViewVariant('archive_card', noActionParametersSchema),
  actionViewVariant('unarchive_card', noActionParametersSchema),
  actionViewVariant('delete_card', noActionParametersSchema),
  actionViewVariant('rename_card', renameCardParametersSchema),
  actionViewVariant('set_due_date', dueDateParametersSchema),
  actionViewVariant('move_due_date', moveDueDateParametersSchema),
  actionViewVariant('remove_due_date', noActionParametersSchema),
  actionViewVariant('mark_due_date_complete', noActionParametersSchema),
  actionViewVariant('add_label', noActionParametersSchema),
  actionViewVariant('remove_label', noActionParametersSchema),
  actionViewVariant('add_member', memberParametersSchema),
  actionViewVariant('remove_member', memberParametersSchema),
  actionViewVariant('post_comment', postCommentParametersSchema),
  actionViewVariant('create_card', createCardParametersSchema),
  actionViewVariant('create_list', createListParametersSchema),
  actionViewVariant('rename_list', renameListParametersSchema),
  actionViewVariant('sort_list', sortListParametersSchema),
  actionViewVariant('move_all_cards_in_list', moveAllCardsInListParametersSchema),
  actionViewVariant('add_checklist', addChecklistParametersSchema),
  actionViewVariant('remove_checklist', removeChecklistParametersSchema),
  actionViewVariant('check_checklist_item', checklistItemParametersSchema),
  actionViewVariant('uncheck_checklist_item', checklistItemParametersSchema),
  actionViewVariant('set_custom_field_value', customFieldValueParametersSchema),
  actionViewVariant('send_email', sendEmailParametersSchema),
  actionViewVariant('http_request', httpRequestParametersSchema),
]);
export type AutomationAction = z.infer<typeof automationActionSchema>;

export const automationActionSnapshotSchema = z.discriminatedUnion('actionKind', [
  actionSnapshotVariant('move_card', cardPlacementParametersSchema),
  actionSnapshotVariant('copy_card', cardPlacementParametersSchema),
  actionSnapshotVariant('mirror_card', cardPlacementParametersSchema),
  actionSnapshotVariant('archive_card', noActionParametersSchema),
  actionSnapshotVariant('unarchive_card', noActionParametersSchema),
  actionSnapshotVariant('delete_card', noActionParametersSchema),
  actionSnapshotVariant('rename_card', renameCardParametersSchema),
  actionSnapshotVariant('set_due_date', dueDateParametersSchema),
  actionSnapshotVariant('move_due_date', moveDueDateParametersSchema),
  actionSnapshotVariant('remove_due_date', noActionParametersSchema),
  actionSnapshotVariant('mark_due_date_complete', noActionParametersSchema),
  actionSnapshotVariant('add_label', noActionParametersSchema),
  actionSnapshotVariant('remove_label', noActionParametersSchema),
  actionSnapshotVariant('add_member', memberParametersSchema),
  actionSnapshotVariant('remove_member', memberParametersSchema),
  actionSnapshotVariant('post_comment', postCommentParametersSchema),
  actionSnapshotVariant('create_card', createCardParametersSchema),
  actionSnapshotVariant('create_list', createListParametersSchema),
  actionSnapshotVariant('rename_list', renameListParametersSchema),
  actionSnapshotVariant('sort_list', sortListParametersSchema),
  actionSnapshotVariant('move_all_cards_in_list', moveAllCardsInListParametersSchema),
  actionSnapshotVariant('add_checklist', addChecklistParametersSchema),
  actionSnapshotVariant('remove_checklist', removeChecklistParametersSchema),
  actionSnapshotVariant('check_checklist_item', checklistItemParametersSchema),
  actionSnapshotVariant('uncheck_checklist_item', checklistItemParametersSchema),
  actionSnapshotVariant('set_custom_field_value', customFieldValueParametersSchema),
  actionSnapshotVariant('send_email', sendEmailParametersSchema),
  actionSnapshotVariant('http_request', httpRequestParametersSchema),
]);
export type AutomationActionSnapshot = z.infer<typeof automationActionSnapshotSchema>;

const commandDefinitionBaseFields = {
  name: commandNameInputSchema,
  icon: iconInputSchema.nullable().default(null),
  plainText: plainTextInputSchema,
  visibility: automationCommandVisibilitySchema.default('shared'),
  scopeKind: automationCommandScopeKindSchema.default('single_board'),
  actingUserId: ulidSchema.optional(),
} as const;

export const automationCommandDefinitionBodySchema = z.discriminatedUnion('commandKind', [
  z.strictObject({
    commandKind: z.literal('rule'),
    ...commandDefinitionBaseFields,
    trigger: automationRuleTriggerBodySchema,
    actions: z
      .array(automationActionDefinitionSchema)
      .min(1)
      .max(AUTOMATION_ACTION_LIMIT),
  }),
  z.strictObject({
    commandKind: z.literal('card_button'),
    ...commandDefinitionBaseFields,
    actions: z
      .array(automationActionDefinitionSchema)
      .min(1)
      .max(AUTOMATION_ACTION_LIMIT),
  }),
  z.strictObject({
    commandKind: z.literal('board_button'),
    ...commandDefinitionBaseFields,
    actions: z
      .array(automationActionDefinitionSchema)
      .min(1)
      .max(AUTOMATION_ACTION_LIMIT),
  }),
  z.strictObject({
    commandKind: z.literal('calendar_command'),
    ...commandDefinitionBaseFields,
    trigger: automationCalendarTriggerBodySchema,
    schedule: automationScheduleBodySchema,
    actions: z
      .array(automationActionDefinitionSchema)
      .min(1)
      .max(AUTOMATION_ACTION_LIMIT),
  }),
  z.strictObject({
    commandKind: z.literal('due_date_command'),
    ...commandDefinitionBaseFields,
    trigger: automationDueDateTriggerBodySchema,
    dueDateOffsetMinutes: z.number().int(),
    actions: z
      .array(automationActionDefinitionSchema)
      .min(1)
      .max(AUTOMATION_ACTION_LIMIT),
  }),
]);
export type AutomationCommandDefinitionBody = z.infer<
  typeof automationCommandDefinitionBodySchema
>;

export const createAutomationCommandBodySchema = automationCommandDefinitionBodySchema;
export type CreateAutomationCommandBody = z.infer<
  typeof createAutomationCommandBodySchema
>;

export const automationDefinitionPatchBodySchema = automationCommandDefinitionBodySchema;
export type AutomationDefinitionPatchBody = z.infer<
  typeof automationDefinitionPatchBodySchema
>;

export const updateAutomationDefinitionBodySchema = automationDefinitionPatchBodySchema;
export type UpdateAutomationDefinitionBody = z.infer<
  typeof updateAutomationDefinitionBodySchema
>;

export const automationEnabledPatchBodySchema = z.strictObject({
  enabled: z.boolean(),
});
export type AutomationEnabledPatchBody = z.infer<typeof automationEnabledPatchBodySchema>;

export const updateAutomationEnabledBodySchema = automationEnabledPatchBodySchema;
export type UpdateAutomationEnabledBody = z.infer<
  typeof updateAutomationEnabledBodySchema
>;

export const boardAutomationsQuerySchema = z.strictObject({
  commandKind: automationCommandKindSchema.optional(),
});
export type BoardAutomationsQuery = z.infer<typeof boardAutomationsQuerySchema>;

export const automationCommandListQuerySchema = boardAutomationsQuerySchema;
export type AutomationCommandListQuery = z.infer<typeof automationCommandListQuerySchema>;

export const automationCommandListItemSchema = z.object({
  id: ulidSchema,
  boardId: ulidSchema,
  scopeBoardId: ulidSchema.nullable(),
  createdByUserId: ulidSchema,
  actingUserId: ulidSchema.nullable(),
  duplicatedFromCommandId: ulidSchema.nullable(),
  commandKind: automationCommandKindSchema,
  name: commandNameViewSchema,
  icon: iconViewSchema.nullable(),
  plainText: plainTextViewSchema,
  enabled: z.boolean(),
  isBroken: z.boolean(),
  visibility: automationCommandVisibilitySchema,
  scopeKind: automationCommandScopeKindSchema.nullable(),
  schedule: automationScheduleSchema.nullable(),
  dueDateOffsetMinutes: z.number().int().nullable(),
  createdAt: timestampViewSchema,
  updatedAt: timestampViewSchema,
});
export type AutomationCommandListItem = z.infer<typeof automationCommandListItemSchema>;

export const automationCommandSchema = automationCommandListItemSchema.extend({
  trigger: automationTriggerSchema.nullable(),
  actions: z.array(automationActionSchema),
});
export type AutomationCommand = z.infer<typeof automationCommandSchema>;

export const automationCommandGroupSchema = z.object({
  commandKind: automationCommandKindSchema,
  commands: z.array(automationCommandListItemSchema),
});
export type AutomationCommandGroup = z.infer<typeof automationCommandGroupSchema>;

export const boardAutomationsResponseSchema = z.object({
  boardId: ulidSchema,
  groups: z.array(automationCommandGroupSchema),
});
export type BoardAutomationsResponse = z.infer<typeof boardAutomationsResponseSchema>;

export const automationCommandListSchema = z.object({
  commands: z.array(automationCommandSchema),
});
export type AutomationCommandList = z.infer<typeof automationCommandListSchema>;

export const automationCommandResponseSchema = automationCommandSchema;
export type AutomationCommandResponse = z.infer<typeof automationCommandResponseSchema>;

export const automationButtonListSchema = z.object({
  buttons: z.array(automationCommandListItemSchema),
});
export type AutomationButtonList = z.infer<typeof automationButtonListSchema>;

export const automationButtonRunBodySchema = z.strictObject({
  confirmDestructive: z.boolean().default(false),
});
export type AutomationButtonRunBody = z.infer<typeof automationButtonRunBodySchema>;

export const automationEnabledPatchResponseSchema = z.object({
  id: ulidSchema,
  enabled: z.boolean(),
  updatedAt: timestampViewSchema,
});
export type AutomationEnabledPatchResponse = z.infer<
  typeof automationEnabledPatchResponseSchema
>;

export const automationRunActionSchema = z.object({
  id: ulidSchema,
  runId: ulidSchema,
  actionPosition: rankViewSchema,
  succeeded: z.boolean(),
  errorText: z.string().min(1).nullable(),
  createdAt: timestampViewSchema,
  updatedAt: timestampViewSchema,
});
export type AutomationRunAction = z.infer<typeof automationRunActionSchema>;

export const automationRunSchema = z.object({
  id: ulidSchema,
  commandId: ulidSchema,
  boardId: ulidSchema,
  cardId: ulidSchema.nullable(),
  actorUserId: ulidSchema.nullable(),
  entryKind: automationRunEntryKindSchema,
  outcome: automationRunOutcomeSchema.nullable(),
  detailText: z.string().min(1).nullable(),
  occurredAt: timestampViewSchema,
  createdAt: timestampViewSchema,
  updatedAt: timestampViewSchema,
});
export type AutomationRun = z.infer<typeof automationRunSchema>;

export const automationRunReceiptSchema = z.object({
  run: automationRunSchema,
  actions: z.array(automationRunActionSchema),
});
export type AutomationRunReceipt = z.infer<typeof automationRunReceiptSchema>;

export const automationButtonRunReceiptSchema = automationRunReceiptSchema;
export type AutomationButtonRunReceipt = z.infer<typeof automationButtonRunReceiptSchema>;

export const automationCommandDiagnosticSchema = z.object({
  commandId: ulidSchema,
  reason: automationDiagnosticReasonSchema,
  detailText: detailTextViewSchema,
  lastRunId: ulidSchema.nullable(),
  lastEvaluatedAt: timestampViewSchema.nullable(),
  updatedAt: timestampViewSchema,
});
export type AutomationCommandDiagnostic = z.infer<
  typeof automationCommandDiagnosticSchema
>;

export const automationBrokenStateSchema = z.discriminatedUnion('isBroken', [
  z.object({
    isBroken: z.literal(false),
    diagnostics: z.null(),
  }),
  z.object({
    isBroken: z.literal(true),
    diagnostics: automationCommandDiagnosticSchema,
  }),
]);
export type AutomationBrokenState = z.infer<typeof automationBrokenStateSchema>;

export const automationCommandLifecycleResponseSchema = z.object({
  id: ulidSchema,
  commandKind: automationCommandKindSchema,
  name: commandNameViewSchema,
  enabled: z.boolean(),
  isBroken: z.boolean(),
  diagnostics: automationCommandDiagnosticSchema.nullable(),
  visibility: automationCommandVisibilitySchema,
  updatedAt: timestampViewSchema,
});
export type AutomationCommandLifecycleResponse = z.infer<
  typeof automationCommandLifecycleResponseSchema
>;

export const enableAutomationCommandBodySchema = automationEnabledPatchBodySchema;
export type EnableAutomationCommandBody = z.infer<
  typeof enableAutomationCommandBodySchema
>;

export const enableAutomationCommandResponseSchema =
  automationCommandLifecycleResponseSchema;
export type EnableAutomationCommandResponse = z.infer<
  typeof enableAutomationCommandResponseSchema
>;

export const deleteAutomationCommandBodySchema = z.strictObject({});
export type DeleteAutomationCommandBody = z.infer<
  typeof deleteAutomationCommandBodySchema
>;

export const deleteAutomationCommandResponseSchema =
  automationCommandLifecycleResponseSchema.extend({
    deletedAt: timestampViewSchema,
  });
export type DeleteAutomationCommandResponse = z.infer<
  typeof deleteAutomationCommandResponseSchema
>;

export const duplicateAutomationCommandBodySchema = z.strictObject({
  destinationBoardId: ulidSchema.nullable().default(null),
  name: commandNameInputSchema.optional(),
});
export type DuplicateAutomationCommandBody = z.infer<
  typeof duplicateAutomationCommandBodySchema
>;

export const automationUnresolvedReferenceSchema = z.object({
  referenceKind: automationReferenceKindSchema,
  sourceId: ulidSchema.nullable(),
  sourceName: z.string().min(1).max(AUTOMATION_COMMAND_NAME_MAX_LENGTH).nullable(),
  sourcePosition: rankViewSchema.nullable(),
  reason: automationUnresolvedReferenceReasonSchema,
  actionPosition: rankViewSchema.nullable(),
});
export type AutomationUnresolvedReference = z.infer<
  typeof automationUnresolvedReferenceSchema
>;

export const duplicateAutomationCommandResponseSchema = z.object({
  command: automationCommandSchema,
  unresolvedReferences: z.array(automationUnresolvedReferenceSchema),
  diagnostics: automationCommandDiagnosticSchema.nullable(),
});
export type DuplicateAutomationCommandResponse = z.infer<
  typeof duplicateAutomationCommandResponseSchema
>;

// ---------------------------------------------------------------------------
// `GET /automations/:id/log` — the diagnostics the log could not name.
//
// `FR-AUTO-013` Behaviour asks for *"a log listing **every run** in reverse
// chronological order with a timestamp, **the card or board it acted on**, each
// action taken and its outcome"*, AC1 for *"three entries appear newest first,
// **each naming the affected card**"*, and AC3 for *"an entry records that the
// command was changed **and by whom**"*. `FR-AUTO-022` Behaviour asks that
// *"Failures are logged **with the returned status**"*.
//
// A ULID is not a name, is not "the card", and is not "whom". Everything below
// exists so the route can answer those four clauses without the reader having
// to resolve an opaque id themselves — and so that the second page of a log
// that claims to cover ninety days can actually be reached.
//
// ⚠ **DECLARATION ORDER IS LOAD-BEARING.** `const` at module scope is in the
// temporal dead zone until its initialiser runs, so everything here must
// precede `automationLogQuerySchema`, `automationCommandLogActionSchema` and
// the two log-entry schemas that reference it. Reordered, this module throws a
// TDZ `ReferenceError` at *import* — every route 500s, and it fails at boot
// rather than in a test. `./notification`'s codec header records the same trap.
// ---------------------------------------------------------------------------

/**
 * How readable the card an automation run acted on is to the reader of this
 * log — a **board admin of the run's own board** (`requireBoardAdminRole`).
 *
 * ⚠ **`'unreadable'` is not "the card is gone".** `fk_automation_runs_card_id`
 * is `ON DELETE SET NULL` and `cards` is soft-deleted, so a card that was
 * deleted still has its row and still has its `title varchar(512) NOT NULL` —
 * the log can always name it, and naming what a run acted on *is* the
 * requirement. `'unreadable'` means the card has since **moved to another
 * board** (or to an inbox, `cards.board_id IS NULL`), which the reader was
 * never authorised to see: a log row must not become a cross-board title
 * oracle.
 *
 * ⚠ **`navigable` is deliberately NOT a second field.** It is
 * `state === 'live' || state === 'archived'`, and two encodings of one fact
 * drift. `activityCardRefSchema` carries an explicit `navigable` because it has
 * no `state` to derive it from; this shape has one.
 */
export const automationLogCardStateSchema = z.enum([
  'live',
  'archived',
  'deleted',
  'unreadable',
]);
export type AutomationLogCardState = z.infer<typeof automationLogCardStateSchema>;

/**
 * The card an automation run acted on, named rather than pointed at.
 *
 * ⚠ **THE WHOLE POINT IS THAT THERE IS NO ID FALLBACK.** Before this chunk the
 * web rendered `entry.cardId` into a sentence slot and a person read
 * `01JQ8Z…` where a card title belonged. Every hard case below gets a *named
 * state* instead: nothing here ever degrades to showing the id, and a renderer
 * that finds `title === null` renders a phrase, never `id`.
 *
 * ⚠ **Mirrored on `activityCardRefSchema`, deliberately NOT imported from
 * `./activity`.** Two reasons, and the second is the load-bearing one: that
 * shape has three fields and no fourth state, and `./activity` is owned by a
 * concurrent chunk — importing across the boundary is the collision this
 * fan-out exists to prevent.
 *
 * ⚠ **`title` and `shortLink` are null *if and only if*
 * `state === 'unreadable'`, and `.superRefine` enforces it rather than a
 * comment.** A flat object of independent nullables lets
 * `{ title: null, state: 'live' }` parse cleanly, which is precisely the
 * *lies-rather-than-omits* shape this chunk exists to end: a renderer would
 * show the withholding phrase for a card it is allowed to name. The route's
 * own `.parse()` is the guard, so the guard has to be able to fail.
 */
export const automationLogCardRefSchema = z
  .object({
    id: ulidSchema,
    /**
     * `cards.title varchar(512)` — bounded by `CARD_TITLE_MAX_LENGTH` from
     * `./board`, imported rather than restated because that constant already
     * describes this column.
     *
     * No `.min(1)`: the column is `NOT NULL` but nothing forbids the empty
     * string, and refusing a real row would be a 500 on a read.
     */
    title: z.string().max(CARD_TITLE_MAX_LENGTH).nullable(),
    /** `cards.short_link` — what `cardPath()` in `./address` builds a URL from. */
    shortLink: z.string().max(SHORT_LINK_MAX_LENGTH).nullable(),
    state: automationLogCardStateSchema,
  })
  .superRefine((value, ctx) => {
    const withheld = value.state === 'unreadable';
    if ((value.title === null) !== withheld) {
      ctx.addIssue({
        code: 'custom',
        path: ['title'],
        message:
          "title is null if and only if state is 'unreadable' — a readable card is always named, and a withheld one never is",
      });
    }
    if ((value.shortLink === null) !== withheld) {
      ctx.addIssue({
        code: 'custom',
        path: ['shortLink'],
        message:
          "shortLink is null if and only if state is 'unreadable' — it travels with the title it addresses",
      });
    }
  });
export type AutomationLogCardRef = z.infer<typeof automationLogCardRefSchema>;

/**
 * Recover the HTTP status of a failed `http_request` action from the sentence
 * the executor stored in `automation_run_actions.error_text`.
 *
 * `FR-AUTO-022` AC4 wants *"the failure … recorded in the command log **with
 * the status returned**"*. Today the status exists only inside English:
 * `executeAutomationHttpAction`'s non-ok branch writes
 * `` `HTTP request returned ${response.status}.` `` and nothing else on the row
 * carries the number. This function is what makes `httpStatus` **true for
 * every failure already on disk** rather than only for rows written after a
 * future executor change.
 *
 * ⚠ **Anchored and range-checked inside itself, not merely by the schema it
 * feeds.** If this could return a number `automationCommandLogActionSchema`
 * then rejects, the route's own `.parse()` would turn a log read into a
 * **500** — the class of bug the cursor codec's guard exists to prevent, in a
 * second place. So the pattern is anchored at both ends, admits exactly three
 * digits, and the result is bounded 100–599 before it is returned.
 *
 * ⚠ **It must not assume that a non-null `errorText` implies a failure.** This
 * fallback exists for rows written before `parameters_snapshot.httpStatus`.
 * New successful HTTP actions store their status in the snapshot and keep
 * `errorText` null; `succeeded` remains the field that carries outcome.
 *
 * Anything else — a timeout's `'HTTP request timed out.'`, a quota sentence, a
 * bare action failure, `null` — yields `null`, which means **"this run recorded
 * no status"** and never HTTP 0.
 */
export function automationHttpStatusFromErrorText(text: string | null): number | null {
  if (text === null) return null;
  const match = /^HTTP request returned (\d{3})\.$/.exec(text);
  if (!match?.[1]) return null;
  const status = Number.parseInt(match[1], 10);
  if (!Number.isInteger(status) || status < 100 || status > 599) return null;
  return status;
}

// ---------------------------------------------------------------------------
// The log's paging cursor — the fifth module-private copy of this codec.
//
// ⚠ **COPIED RATHER THAN IMPORTED, AND THE DUPLICATION IS THE POINT.**
// `./comment`, `./notification`, `./board-archive` and `./activity` each carry
// their own twenty lines, and each header states the same reason: a shared
// codec couples two tables' sort keys, and the day one changes the other
// changes silently — a paging stream that skips or repeats rows with a 200 and
// a well-formed body. This is the fifth, for that reason and no other.
//
// ⚠ **IT CARRIES `./notification`'s MEASURED `POSTGRES_TIMESTAMPTZ` GUARD, not
// `./comment`'s length-only bound.** Without it a fabricated cursor carrying
// the eight characters `notatime` decodes cleanly, splits into exactly two
// parts, passes the ULID check, and reaches `'notatime'::timestamptz` in the
// `WHERE` — where Postgres raises `invalid input syntax for type timestamp with
// time zone` and the caller gets a **500** from the one function whose entire
// job is to produce a **422**.
//
// ⚠ **NO `type` DISCRIMINATOR.** `./board-archive` needs one because a single
// codec serves two streams; this route pages **one** table, scoped by a
// `command_id` the path already fixes, so there is nothing to disambiguate.
// ---------------------------------------------------------------------------

/**
 * The log's opaque paging cursor.
 *
 * ⚠ **Deliberately the *same object* as the bound `automationLogQuerySchema`
 * and `automationCommandLogResponseSchema` already use**, exported under a
 * name rather than re-declared. A second `z.string().min(1).max(128)` beside
 * the first is two bounds that can drift, and the drift would be a cursor the
 * response can emit and the query cannot accept — "load more" answering 422 on
 * a cursor the server itself produced.
 *
 * This says only "a plausible cursor-shaped string". `decodeAutomationLogCursor`
 * is what refuses a malformed one, and it **must be called**: base64 decoding
 * does not fail on garbage.
 */
export const automationLogCursorSchema = cursorViewSchema;

/**
 * How Postgres renders a `timestamptz(3)`, permissively — `./notification`'s
 * measured pattern, copied with its reasoning intact.
 *
 * `now()::timestamptz(3)::text` renders `2026-08-12 03:38:59.462+00`, and the
 * fractional part is **omitted entirely** when it is zero. The offset is the
 * session's `TimeZone`. The `T`/`Z` alternatives are admitted because an ISO
 * string is a legitimate thing for a caller to have round-tripped through a
 * `Date`, and Postgres casts it identically.
 *
 * ⚠ It validates the **shape**, not the calendar: `9999-99-99 99:99:99+00`
 * passes here and would still be a 500. Closing that would mean parsing dates
 * in a package that must not depend on a date library, and the residual hole is
 * a fabricated cursor that is *nearly* a timestamp — vastly narrower than the
 * one the pattern closes, which any typo produces.
 */
const POSTGRES_TIMESTAMPTZ =
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d{1,6})?([+-]\d{2}(:\d{2}(:\d{2})?)?|Z)?$/;

/**
 * The decoded cursor: the exact `(occurred_at, id)` pair of the last entry on
 * the page that was served.
 *
 * ⚠ **`occurredAt`, NOT `createdAt` — and the two are different columns on this
 * table.** The log's `ORDER BY` is `(occurred_at DESC, id DESC)`, matching
 * `idx_automation_runs_command_id_occurred_at`, while `automation_runs` also
 * carries a `created_at`. Both are timestamp strings, so a codec copied from
 * `./notification` without the rename compiles, round-trips, and pages against
 * the wrong column — rows out of order across a boundary, with nothing red.
 * `archiveCursorPayloadSchema.sortKey`'s note records the same confusion.
 *
 * Module-private on purpose: a caller receives this shape from
 * `decodeAutomationLogCursor` and never constructs one from a cursor by hand.
 */
const automationLogCursorPayloadSchema = z.strictObject({
  occurredAt: z.string().min(1).max(64).regex(POSTGRES_TIMESTAMPTZ),
  id: ulidSchema,
});

/**
 * The base64url alphabet, unpadded.
 *
 * ⚠ **Hand-rolled rather than `btoa`/`atob` or `Buffer`, for the measured
 * reasons `./comment` recorded and `./notification` inherited**: `Buffer` is
 * Node-only and this package is bundled into `apps/web` by Vite, which does not
 * polyfill it, while `btoa`/`atob` exist at runtime everywhere but are **not
 * declared** under this workspace's `lib: ["ES2023"]` with no DOM lib, so `tsc`
 * answers `TS2304: Cannot find name 'btoa'`.
 */
const BASE64URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Latin-1 (here: ASCII) in, unpadded base64url out. */
function toBase64Url(input: string): string {
  let out = '';
  for (let i = 0; i < input.length; i += 3) {
    const b0 = input.charCodeAt(i);
    const b1 = input.charCodeAt(i + 1);
    const b2 = input.charCodeAt(i + 2);
    const has1 = !Number.isNaN(b1);
    const has2 = !Number.isNaN(b2);
    const triple = (b0 << 16) | ((has1 ? b1 : 0) << 8) | (has2 ? b2 : 0);
    out += BASE64URL_ALPHABET.charAt((triple >>> 18) & 63);
    out += BASE64URL_ALPHABET.charAt((triple >>> 12) & 63);
    if (has1) out += BASE64URL_ALPHABET.charAt((triple >>> 6) & 63);
    if (has2) out += BASE64URL_ALPHABET.charAt(triple & 63);
  }
  return out;
}

/**
 * Unpadded base64url in, latin-1 out. Throws on a character outside the
 * alphabet and on a length no base64 string can have (`length % 4 === 1`).
 *
 * ⚠ It does **not** throw on every string that merely fails to mean anything —
 * `'abc'` decodes to two bytes of noise quite happily — which is why
 * `decodeAutomationLogCursor` validates the *shape* afterwards rather than
 * treating a successful decode as proof of anything.
 */
function fromBase64Url(input: string): string {
  if (input.length % 4 === 1) throw new Error('not base64url');
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const character of input) {
    const value = BASE64URL_ALPHABET.indexOf(character);
    if (value < 0) throw new Error('not base64url');
    // Masked to sixteen bits deliberately — only the low twelve are ever read
    // back, and an unmasked accumulator would run past thirty-two bits, where
    // JavaScript's bitwise operators truncate.
    buffer = ((buffer << 6) | value) & 0xffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((buffer >>> bits) & 0xff);
    }
  }
  return out;
}

/**
 * Turn the last entry on a page of the automation log into the cursor that
 * fetches the next one.
 *
 * ⚠ **VALUE-CARRYING and self-contained — base64url of
 * `` `${occurredAt}|${id}` `` — and emphatically NOT `?before=<runId>`.** This
 * function takes **no database handle**; that is the property, not an accident.
 *
 * ⚠ **ENCODE FROM THE LAST ROW *KEPT*, NEVER FROM THE DISCARDED `limit + 1`th
 * ROW** the query fetched to discover whether another page exists. From the
 * extra row, every page boundary silently skips exactly one entry — a log that
 * loses one run per page, with a 200 and a well-formed body.
 *
 * ⚠ **`occurredAt` is carried as the exact string the driver returned**, in
 * Postgres' spelling, which is why the route's `WHERE` casts it
 * (`$cursorOccurredAt::timestamptz`).
 */
export function encodeAutomationLogCursor(row: {
  occurredAt: string;
  id: string;
}): string {
  const raw = `${row.occurredAt}|${row.id}`;
  // Guard the one assumption the codec makes. Both halves are ASCII by
  // construction (a Postgres timestamp and a Crockford base32 ULID), so this
  // can only fire if a caller invents a row — in which case failing loudly
  // beats emitting a cursor that decodes to mojibake.
  for (let i = 0; i < raw.length; i += 1) {
    if (raw.charCodeAt(i) > 0xff) {
      throw new Error('automation log cursor components must be latin-1');
    }
  }
  return toBase64Url(raw);
}

/**
 * Turn a cursor back into the `(occurred_at, id)` pair it names.
 *
 * ⚠ **A MALFORMED CURSOR IS A 422, NEVER "START FROM THE TOP".** Silently
 * falling back to the first page turns "load older" into an infinite loop that
 * re-serves page one for ever — a 200 with a well-formed body, so nothing
 * anywhere fails. That is why this throws an `ApiError('validation_failed')`
 * rather than a plain `Error` a route must remember to catch: the rejected
 * alternative leaves the refusal a matter of handler discipline, and a
 * forgotten `try` is a **500** instead of the 422.
 *
 * ⚠ **The shape check after the decode is the whole defence, because base64
 * decoding does not fail on garbage.** All four of these are refusals: a
 * character outside the alphabet, an impossible length, a decoded string that
 * does not split into **exactly two** parts on `|`, and a payload whose halves
 * are not a Postgres timestamp and a ULID.
 *
 * `|` is safe as the separator because neither component can contain one — a
 * `timestamptz` renders as digits, hyphens, colons, a dot, a space and a sign,
 * and Crockford base32 is `[0-9A-HJKMNP-TV-Z]`.
 *
 * ⚠ **The route must decode it AFTER the 404 and the 403**, never before: a
 * caller outside the read scope must not learn from a 422 that the command
 * exists. The house order within a board is 404, then 403, then 409 — and this
 * 422 is last.
 */
export function decodeAutomationLogCursor(cursor: string): {
  occurredAt: string;
  id: string;
} {
  const invalid = (): never => {
    throw new ApiError('validation_failed', 'Malformed cursor', {
      cursor: ['must be a cursor returned by a previous page of this automation log'],
    });
  };

  let raw: string;
  try {
    raw = fromBase64Url(cursor);
  } catch {
    return invalid();
  }

  const parts = raw.split('|');
  if (parts.length !== 2) return invalid();

  const parsed = automationLogCursorPayloadSchema.safeParse({
    occurredAt: parts[0],
    id: parts[1],
  });
  if (!parsed.success) return invalid();

  return parsed.data;
}

/**
 * `GET /automations/:id/log` query.
 *
 * ⚠ **`cursor` HAS ALWAYS BEEN DECLARED HERE AND WAS, UNTIL THIS CHUNK, READ
 * AND THROWN AWAY.** The route forwarded only `limit`, the read had no cursor
 * parameter, and the response's `nextCursor` was a hardcoded `null` — so a
 * reader paging the log saw page one and was told, in a well-formed 200, that
 * it was the whole log. Of the three available answers — implement it, reject
 * it, accept-and-ignore it — the third is strictly the worst, and it is what
 * shipped.
 *
 * It is now **honoured**: the route passes it through
 * `decodeAutomationLogCursor` and pages the keyset
 * `(occurred_at, id) < (cursor.occurredAt, cursor.id)`, which is exactly
 * `idx_automation_runs_command_id_occurred_at`. Implementing rather than
 * rejecting is what `FR-AUTO-013`'s *"every run"* requires: rejecting the
 * parameter would make the API honest and leave the requirement unmet.
 *
 * ⚠ **A malformed cursor is a 422, decided AFTER the 404 and the 403** — a
 * caller outside the read scope must not learn from a cursor error that the
 * command exists.
 *
 * Absent means "the newest page". The bound is the same object as
 * `automationLogCursorSchema`, not a second copy of it.
 */
export const automationLogQuerySchema = z.strictObject({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(AUTOMATION_LOG_PAGE_SIZE_MAX)
    .default(AUTOMATION_LOG_PAGE_SIZE_DEFAULT),
  cursor: automationLogCursorSchema.optional(),
});
export type AutomationLogQuery = z.infer<typeof automationLogQuerySchema>;

/**
 * One action inside a log entry: the snapshot of what the action was, plus what
 * happened when it ran.
 *
 * ⚠ **UNCHANGED BY THIS CHUNK, AND THAT IS A CORRECTION.** The first pass
 * added `httpStatus` here, in place, on the strength of a footprint scan that
 * counted this schema's *direct references* — four, all in this file — and
 * concluded "one serialising route". **Reference count is not
 * serialising-route count.** This schema is reached transitively by
 * `automationCommandDefinitionChangeLogEntrySchema`, which
 * `automationAdminDisableResponseSchema` embeds, which
 * `POST /automations/:id/admin-disable` returns
 * (`apps/server/src/routes/automations.ts:492`, `:510`) — a second route, for
 * `FR-AUTO-017`, whose projection this chunk does not own and must not break.
 *
 * The log's own extension lives in `automationLogActionSchema` below.
 */
export const automationCommandLogActionSchema = z.intersection(
  automationActionSnapshotSchema,
  z.object({
    id: ulidSchema,
    runId: ulidSchema,
    sourceActionId: ulidSchema.nullable(),
    succeeded: z.boolean(),
    errorText: detailTextViewSchema.nullable(),
    createdAt: timestampViewSchema,
    updatedAt: timestampViewSchema,
  }),
);
export type AutomationCommandLogAction = z.infer<typeof automationCommandLogActionSchema>;

/**
 * A run (or a blocked would-be run) on the per-command log.
 *
 * ⚠ **UNCHANGED BY THIS CHUNK.** `card` and `actor` live on
 * `automationLogRunEntrySchema` below, not here — the house rule is **extend a
 * shipped schema on a copy, never widen it in place**, and its sibling arm
 * below is provably serialised by a second route.
 */
export const automationCommandRunLogEntrySchema = automationRunSchema.extend({
  entryKind: z.enum([
    'run',
    'quota_blocked',
    'permission_blocked',
    'missing_reference_blocked',
    'cap_exceeded',
    'loop_suppressed',
  ]),
  actions: z.array(automationCommandLogActionSchema),
});
export type AutomationCommandRunLogEntry = z.infer<
  typeof automationCommandRunLogEntrySchema
>;

/**
 * A definition change or an admin disable on the per-command log.
 *
 * ⚠ **UNCHANGED BY THIS CHUNK, AND THIS IS THE ONE THE FIRST PASS GOT WRONG.**
 * `card` and `actor` were added here in place, and the schema-footprint table
 * recorded it as safe on the strength of a *direct reference* count — ten
 * matches, all inside `packages/shared`. Measured again **transitively**: this
 * schema is embedded in `automationAdminDisableResponseSchema` (below, as
 * `logEntry`), which `POST /automations/:id/admin-disable` both declares and
 * `.parse()`s — `apps/server/src/routes/automations.ts:492` and `:510`, for
 * `FR-AUTO-017`. That route's row is built by `adminDisableAutomationCommand`
 * in `apps/server/src/automation/persistence.ts`, a file this chunk does not
 * own, which projects no card and no actor. Two required fields here therefore
 * turn that route's own `.parse()` into a **500 on every admin disable**, for a
 * requirement this chunk was not asked to touch.
 *
 * **Reference count is not serialising-route count.** The measurement that
 * decides is transitive embedding into other response schemas, and it is the
 * one the first pass did not make.
 */
export const automationCommandDefinitionChangeLogEntrySchema = automationRunSchema.extend(
  {
    entryKind: z.enum(['definition_changed', 'admin_disabled']),
    outcome: z.null(),
    actions: z.array(automationCommandLogActionSchema).length(0),
  },
);
export type AutomationCommandDefinitionChangeLogEntry = z.infer<
  typeof automationCommandDefinitionChangeLogEntrySchema
>;

// ---------------------------------------------------------------------------
// The log-only extensions — `GET /automations/:id/log` and nothing else.
//
// ⚠ **THE THREE NEW FIELDS ARE `.optional()` HERE AND REQUIRED ON THE `Strict`
// TWINS BELOW, AND THE PAIR IS THE POINT.** The permissive schemas are what
// `apps/web/src/lib/automations.ts` parses a response with, and what the
// exported `AutomationCommandLogResponse` type describes; the strict ones are
// what the route `.parse()`es its **own projection** with before answering. A
// projection that forgets to join the card therefore throws **at the route** —
// the loud failure a required-and-nullable field was wanted for — with no
// out-of-list fixture edit, because the type the fixtures are annotated with is
// derived from the permissive pair.
//
// ⚠ **A renderer must treat `undefined` and `null` alike as "no name to show",
// and must never fall back to the id** — falling back to the id is exactly what
// `FE-70.2` exists to end. `automationLogCardRefSchema.state` is where "the
// card is gone" is said; absence is not.
// ---------------------------------------------------------------------------

/**
 * `FR-AUTO-022` Behaviour — *"Failures are logged **with the returned
 * status**"* — and AC4, *"the failure is recorded in the command log **with the
 * status returned**"*, as a field rather than as a number buried in an English
 * sentence a client would have to parse.
 *
 * ⚠ **`null` means "this run recorded no status", NEVER HTTP 0.** It is `null`
 * for every action that is not an `http_request`, for a timeout or a network
 * failure (which has no status to record), and for legacy rows written before
 * `parameters_snapshot.httpStatus`. New successful `http_request` actions
 * store their returned `2xx` status in that additive JSON key, so no migration
 * is needed and the action outcome remains separate.
 *
 * ⚠ **Do not read this as an outcome.** `succeeded` is the field that carries
 * whether the action worked; a `2xx` here would not contradict
 * `succeeded: false`, and a `null` does not imply success.
 *
 * Module-private and declared once: the bound is quoted by both the permissive
 * and the strict action schema, and two copies of `100..599` would drift.
 */
const automationLogHttpStatusSchema = z.number().int().min(100).max(599).nullable();

/**
 * One action inside a **log** entry — the shipped base plus the HTTP status.
 *
 * ⚠ **A `z.intersection` over an intersection, because
 * `automationActionSnapshotSchema` is a discriminated union and there is no
 * `.extend()` on an intersection.** Intersecting the shipped schema *is* the
 * copy; restating its seven fields would be the drift.
 */
export const automationLogActionSchema = z.intersection(
  automationCommandLogActionSchema,
  z.object({ httpStatus: automationLogHttpStatusSchema.optional() }),
);
export type AutomationLogAction = z.infer<typeof automationLogActionSchema>;

/**
 * The same action as **the route** must be able to produce it: `httpStatus`
 * required-and-nullable, so a projection that drops the field throws at
 * `.parse()` rather than serialising a silently statusless failure.
 */
export const automationLogActionStrictSchema = z.intersection(
  automationCommandLogActionSchema,
  z.object({ httpStatus: automationLogHttpStatusSchema }),
);
export type AutomationLogActionStrict = z.infer<typeof automationLogActionStrictSchema>;

/**
 * `FR-AUTO-013` Behaviour — *"**the card or board it acted on**"* — and AC1,
 * *"each naming the affected card"*.
 *
 * ⚠ **`null` means "this run had no card", and nothing else.**
 * `fk_automation_runs_card_id` is `ON DELETE SET NULL` against a
 * **soft**-deleted table, so a card that was deleted still has its row and
 * still has its title; `card_id IS NULL` is exactly a board button or a
 * board-scoped rule. A client must read `card === null` as "name the board
 * instead", never as "the card went away" — `automationLogCardRefSchema.state`
 * is where *that* is said.
 *
 * Declared once and shared by the permissive and the strict arm, so the two can
 * differ in presence and never in shape.
 */
const automationLogEntryCardField = automationLogCardRefSchema.nullable();

/**
 * `FR-AUTO-013` AC3 — *"an entry records that the command was changed **and by
 * whom**"*.
 *
 * ⚠ **`LEFT JOIN users`, with NO join to `board_members`** —
 * `activityEntrySchema.actor`'s ruling, which binds here word for word because
 * `FR-COLLAB-018` AC4 asks the same thing of the same kind of row: *"given an
 * actor has been removed from the board, when their old entries render, then
 * their name and avatar still resolve."* An INNER join to `board_members` would
 * hide exactly the history the AC names. Before this chunk the web looked the
 * actor up in the board member list it happened to hold and rendered the raw id
 * on a miss — which fired for a removed member, an actor on another board, and
 * simply a panel opened before members loaded.
 *
 * ⚠ **`null` iff `actorUserId === null`.** `fk_automation_runs_actor_user_id`
 * is `ON DELETE SET NULL` and `users.display_name` is `varchar(128) NOT NULL`,
 * so a non-null `actor_user_id` **provably** resolves to a name. `null` means a
 * purged account or no acting member, and nothing else.
 *
 * `publicProfileSchema` is reused whole, exactly as `commentSchema.author`
 * does, so an actor cannot acquire an `email` by accident.
 */
const automationLogEntryActorField = publicProfileSchema.nullable();

/**
 * A run on the log, named rather than pointed at.
 *
 * ⚠ **`cardId` and `actorUserId` stay on the wire beside them**, inherited from
 * `automationRunSchema`. They are not replaced and not deprecated: removing a
 * shipped field would weaken the contract, and a client comparing identity
 * rather than rendering a name still needs the id.
 */
export const automationLogRunEntrySchema = automationCommandRunLogEntrySchema.extend({
  card: automationLogEntryCardField.optional(),
  actor: automationLogEntryActorField.optional(),
  actions: z.array(automationLogActionSchema),
});
export type AutomationLogRunEntry = z.infer<typeof automationLogRunEntrySchema>;

/**
 * A definition change or an admin disable **as the log renders it**.
 *
 * ⚠ **The same two fields as the run arm, in the same shapes.** A union whose
 * arms carry different fields makes a renderer branch on the arm before it can
 * read a name, and `FR-AUTO-013` AC3 — *"an entry records that the command was
 * changed **and by whom**"* — is asked of **this** arm specifically. `card` is
 * here too rather than omitted: it is almost always `null` on a definition
 * change, and "almost always" is not "always".
 *
 * ⚠ The **admin-disable response** keeps the base schema, not this one: that
 * route is `FR-AUTO-017`'s, and its projection lives in a file this chunk does
 * not own.
 */
export const automationLogDefinitionChangeEntrySchema =
  automationCommandDefinitionChangeLogEntrySchema.extend({
    card: automationLogEntryCardField.optional(),
    actor: automationLogEntryActorField.optional(),
  });
export type AutomationLogDefinitionChangeEntry = z.infer<
  typeof automationLogDefinitionChangeEntrySchema
>;

/** The run arm as the route must produce it — both fields present. */
export const automationLogRunEntryStrictSchema =
  automationCommandRunLogEntrySchema.extend({
    card: automationLogEntryCardField,
    actor: automationLogEntryActorField,
    actions: z.array(automationLogActionStrictSchema),
  });
export type AutomationLogRunEntryStrict = z.infer<
  typeof automationLogRunEntryStrictSchema
>;

/** The definition-change arm as the route must produce it — both present. */
export const automationLogDefinitionChangeEntryStrictSchema =
  automationCommandDefinitionChangeLogEntrySchema.extend({
    card: automationLogEntryCardField,
    actor: automationLogEntryActorField,
  });
export type AutomationLogDefinitionChangeEntryStrict = z.infer<
  typeof automationLogDefinitionChangeEntryStrictSchema
>;

export const automationCommandLogEntrySchema = z.union([
  automationLogRunEntrySchema,
  automationLogDefinitionChangeEntrySchema,
]);
export type AutomationCommandLogEntry = z.infer<typeof automationCommandLogEntrySchema>;

export const automationCommandLogRunStateSchema = z.enum(['never_run', 'has_entries']);
export type AutomationCommandLogRunState = z.infer<
  typeof automationCommandLogRunStateSchema
>;

export const automationCommandLogResponseSchema = z.object({
  command: automationCommandLifecycleResponseSchema,
  runState: automationCommandLogRunStateSchema,
  entries: z.array(automationCommandLogEntrySchema),
  /**
   * The window the product **guarantees** to keep — a floor on completeness,
   * not a ceiling on disclosure.
   *
   * ⚠ **`since` is reported and is deliberately NOT a `WHERE` predicate**, and
   * now that paging is real that decides where paging terminates. There is no
   * purge job for `automation_runs`, so rows older than the window still exist;
   * hiding rows that are on disk, that the reader is authorised to see, and
   * that answer `FR-AUTO-013`'s *"every run"* would serve nobody and would make
   * the log **less** complete the day a purge job is finally written. Paging
   * walks back to the command's first run. The edge case's promise — *"the log
   * states the window it covers rather than implying completeness"* — is kept
   * by this field, which is what states it.
   */
  retention: z.object({
    windowDays: z.number().int().positive(),
    since: timestampViewSchema,
  }),
  /**
   * The cursor that fetches the next (older) page, or `null` when this is the
   * last one.
   *
   * ⚠ **This field was declared from the start and was a hardcoded `null`** —
   * the lie this chunk exists to end. It now means what it says.
   *
   * ⚠ **A client pages on `nextCursor !== null` alone, NEVER on
   * `entries.length`.** A full page can be the last page, and a short page can
   * have a successor the day a `WHERE` is added. It is encoded from the **last
   * row kept**, never from the discarded `limit + 1`th row the query fetched to
   * discover whether another page exists — from the extra row, every page
   * boundary silently skips exactly one entry.
   */
  nextCursor: automationLogCursorSchema.nullable(),
});
export type AutomationCommandLogResponse = z.infer<
  typeof automationCommandLogResponseSchema
>;

/**
 * The same response as **the route** must be able to produce it — every log
 * entry carrying `card`, `actor` and each action's `httpStatus`.
 *
 * ⚠ **THE ROUTE PARSES WITH THIS ONE; THE CLIENT PARSES WITH THE PERMISSIVE
 * TWIN ABOVE.** That asymmetry is what buys a required field's guarantee — a
 * projection that forgets the card join throws a `ZodError` at
 * `apps/server/src/routes/automations.ts` rather than answering 200 with a
 * quietly absent name — while keeping the three fields `.optional()` on the
 * type that `apps/web/src/lib/automations.test.ts` and
 * `apps/web/src/components/board/BoardMenuPanel.test.tsx` annotate their
 * fixtures with, neither of which is on this chunk's file list.
 *
 * ⚠ **A strict body always satisfies the permissive schema, never the
 * reverse.** So the route may declare either to fastify and must `.parse()`
 * with **this** one: declaring this and parsing with the permissive one would
 * put the check on the serialiser, where a missing key is silently dropped.
 */
export const automationCommandLogResponseStrictSchema =
  automationCommandLogResponseSchema.extend({
    entries: z.array(
      z.union([
        automationLogRunEntryStrictSchema,
        automationLogDefinitionChangeEntryStrictSchema,
      ]),
    ),
  });
export type AutomationCommandLogResponseStrict = z.infer<
  typeof automationCommandLogResponseStrictSchema
>;

export const workspaceAutomationActivityQuerySchema = z.strictObject({
  boardId: ulidSchema.optional(),
  outcome: automationRunOutcomeSchema.optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(AUTOMATION_LOG_PAGE_SIZE_MAX)
    .default(AUTOMATION_LOG_PAGE_SIZE_DEFAULT),
  cursor: cursorViewSchema.optional(),
});
export type WorkspaceAutomationActivityQuery = z.infer<
  typeof workspaceAutomationActivityQuerySchema
>;

export const automationActivityCommandRefSchema = z.object({
  id: ulidSchema,
  commandKind: automationCommandKindSchema,
  name: commandNameViewSchema,
  visibility: automationCommandVisibilitySchema,
});
export type AutomationActivityCommandRef = z.infer<
  typeof automationActivityCommandRefSchema
>;

export const automationActivityBoardRefSchema = z.object({
  id: ulidSchema,
  name: boardNameViewSchema,
});
export type AutomationActivityBoardRef = z.infer<typeof automationActivityBoardRefSchema>;

export const workspaceAutomationActivityEntrySchema = z.object({
  id: ulidSchema,
  command: automationActivityCommandRefSchema,
  board: automationActivityBoardRefSchema,
  cardId: ulidSchema.nullable(),
  actorUserId: ulidSchema.nullable(),
  entryKind: automationRunEntryKindSchema,
  outcome: automationRunOutcomeSchema.nullable(),
  errorSummary: detailTextViewSchema.nullable(),
  occurredAt: timestampViewSchema,
});
export type WorkspaceAutomationActivityEntry = z.infer<
  typeof workspaceAutomationActivityEntrySchema
>;

export const workspaceAutomationActivityResponseSchema = z.object({
  workspaceId: ulidSchema,
  entries: z.array(workspaceAutomationActivityEntrySchema),
  nextCursor: cursorViewSchema.nullable(),
});
export type WorkspaceAutomationActivityResponse = z.infer<
  typeof workspaceAutomationActivityResponseSchema
>;

export const workspaceAutomationUsageResponseSchema = z.object({
  workspaceId: ulidSchema,
  periodStart: dateViewSchema,
  periodEnd: dateViewSchema,
  resetAt: timestampViewSchema,
  runsCount: z.number().int().nonnegative(),
  operationsCount: z.number().int().nonnegative(),
  emailsSentCount: z.number().int().nonnegative(),
  limits: z.object({
    runs: z.literal(AUTOMATION_MONTHLY_RUN_LIMIT),
    operations: z.literal(AUTOMATION_MONTHLY_OPERATION_LIMIT),
    emails: z.literal(AUTOMATION_MONTHLY_EMAIL_LIMIT),
  }),
  updatedAt: timestampViewSchema,
});
export type WorkspaceAutomationUsageResponse = z.infer<
  typeof workspaceAutomationUsageResponseSchema
>;

export const automationAdminReviewQuerySchema = z.strictObject({
  boardId: ulidSchema.optional(),
});
export type AutomationAdminReviewQuery = z.infer<typeof automationAdminReviewQuerySchema>;

export const automationAdminReviewCommandSchema =
  automationCommandLifecycleResponseSchema.extend({
    board: automationActivityBoardRefSchema,
    createdByUserId: ulidSchema,
    actingUserId: ulidSchema.nullable(),
    scopeKind: automationCommandScopeKindSchema.nullable(),
    lastRunAt: timestampViewSchema.nullable(),
    lastOutcome: automationRunOutcomeSchema.nullable(),
  });
export type AutomationAdminReviewCommand = z.infer<
  typeof automationAdminReviewCommandSchema
>;

export const automationAdminReviewResponseSchema = z.object({
  workspaceId: ulidSchema,
  commands: z.array(automationAdminReviewCommandSchema),
});
export type AutomationAdminReviewResponse = z.infer<
  typeof automationAdminReviewResponseSchema
>;

export const automationAdminDisableBodySchema = z.strictObject({
  reason: z
    .string()
    .trim()
    .min(1)
    .max(AUTOMATION_DIAGNOSTIC_DETAIL_MAX_LENGTH)
    .optional(),
});
export type AutomationAdminDisableBody = z.infer<typeof automationAdminDisableBodySchema>;

export const automationAdminDisableResponseSchema = z.object({
  command: automationCommandLifecycleResponseSchema,
  logEntry: automationCommandDefinitionChangeLogEntrySchema,
});
export type AutomationAdminDisableResponse = z.infer<
  typeof automationAdminDisableResponseSchema
>;
